import { useEffect } from "react";
import { createPortal } from "react-dom";
import { FilterValues, IFilterSection } from "@models/IFilterConfig";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import { FilterValue } from "@models/IFilterPresets";
import { OwSidebar } from "../OwSidebar/OwSidebar";
import OwButton from "../OwButton/OwButton";
import { FilterField } from "./FilterField";
import { ChipLabelFormatter } from "./filterBarUtils";
import { OptionLabels } from "./useFilterBar";

type Props = {
    isOpen: boolean;
    sections: IFilterSection[];
    draft: FilterValues;
    draftActiveCount: number;
    activePreset?: IFilterPresetRuntime;
    isModified: boolean;
    canUpdatePreset: boolean;
    isSearching?: boolean;
    optionLabels: OptionLabels;
    translate: ChipLabelFormatter;
    onChange: (key: string, value: FilterValue) => void;
    onOptionLabel: (fieldKey: string, value: string, label: string) => void;
    onSearch: () => void;
    onReset: () => void;
    onUpdatePreset: () => void;
    onSaveAsNew: () => void;
    onClose: () => void;
    /** When set, a "Send a suggestion" link renders under the footer buttons */
    onFeedback?: () => void;
    /** True when no default preset applies (the "All" view opens by default) */
    isAllDefault: boolean;
    /** Set/clear the active preset as the default view (only offered when the preset is editable) */
    onToggleDefault: () => void;
    /** Make the "All" view the default (only offered when no preset is active) */
    onSetAllAsDefault: () => void;
    /** Delete the active preset: closes the drawer, then confirms */
    onDeletePreset: () => void;
};

/**
 * Full-height right-side overlay panel (340px): sticky header, scrollable sectioned body, sticky footer.
 * Rendered into document.body so the backdrop covers the whole page.
 */
export const FilterDrawer = (props: Props) => {
    const { isOpen, onClose, translate } = props;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const subtitleParts: string[] = [translate("n_active", { count: props.draftActiveCount })];

    if (props.activePreset) {
        subtitleParts.push(`${translate("from")} ${props.activePreset.name}${props.isModified ? ` (${translate("modified")})` : ""}`);
    }

    return createPortal(
        <div className={"FilterBar-drawer-host"}>
            <OwSidebar position={"right"} width={"340px"} isOpen={true} onClose={onClose} disableScroll={true}>
                <div className={"FilterBar-drawer d-flex flex-column h-100"}>
                    <div className={"FilterBar-drawer-header d-flex align-items-start"}>
                        <div className={"flex-grow-1"}>
                            <h5 className={"mb-0"}>{translate("filters")}</h5>
                            <small className={"text-muted"}>{subtitleParts.join(" · ")}</small>
                        </div>
                        <button
                            type={"button"}
                            className={"btn btn-sm btn-link FilterBar-close"}
                            aria-label={translate("close")}
                            onClick={onClose}>
                            <i className={"fas fa-times"} />
                        </button>
                    </div>

                    <div className={"FilterBar-drawer-body flex-grow-1"}>
                        {props.sections
                            .filter((section) => section.filters.length > 0)
                            .map((section) => (
                                <div key={section.id} className={"FilterBar-section" + (section.isExclusion ? " exclusion" : "")}>
                                    <div className={"FilterBar-section-title"}>{translate(section.title)}</div>
                                    {section.isExclusion ? (
                                        <div className={"FilterBar-section-help"}>{translate("excluded_from_results")}</div>
                                    ) : null}

                                    {section.filters.map((field) => {
                                        const isToggle = field.type === "toggle";

                                        return (
                                            <div key={field.key} className={"FilterBar-field" + (isToggle ? " toggle" : "")}>
                                                <label
                                                    className={"FilterBar-field-label"}
                                                    htmlFor={isToggle ? undefined : `filter-field-${field.key}`}>
                                                    {translate(field.label)}
                                                </label>
                                                <div className={"FilterBar-field-input"}>
                                                    <FilterField
                                                        field={field}
                                                        value={props.draft[field.key]}
                                                        onChange={props.onChange}
                                                        onEnter={props.onSearch}
                                                        translate={translate}
                                                        isExclusion={section.isExclusion}
                                                        optionLabels={props.optionLabels[field.key]}
                                                        onOptionLabel={props.onOptionLabel}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                    </div>

                    <div className={"FilterBar-drawer-footer"}>
                        <div className={"d-flex align-items-center"}>
                            <OwButton
                                title={"search"}
                                className={"btn-warning flex-grow-1"}
                                leftIconClass={"fas fa-search"}
                                fn={props.onSearch}
                                active={props.isSearching}
                                disableOnActive={true}
                                disableWidthOnClick={true}
                            />
                            <OwButton title={"reset"} className={"btn-outline-secondary ms-2"} fn={props.onReset} />
                        </div>
                        {props.canUpdatePreset || props.draftActiveCount > 0 ? (
                            <div className={"d-flex align-items-center mt-2"}>
                                {props.canUpdatePreset && props.activePreset ? (
                                    <OwButton
                                        title={translate("update_preset", { name: props.activePreset.name })}
                                        skipTranslate={true}
                                        className={"btn-outline-warning flex-grow-1 me-2"}
                                        leftIconClass={"fas fa-save"}
                                        fn={props.onUpdatePreset}
                                    />
                                ) : null}
                                {props.draftActiveCount > 0 ? (
                                    <OwButton
                                        title={"save_as_new_preset"}
                                        className={"btn-outline-secondary flex-grow-1"}
                                        leftIconClass={"fas fa-plus"}
                                        fn={props.onSaveAsNew}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                        {/* Preset options — the same actions as the tab's right-click menu, where people will find them */}
                        {props.activePreset && props.activePreset.editable ? (
                            <div className={"FilterBar-preset-actions d-flex justify-content-between mt-2"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-link btn-sm p-0"}
                                    data-testid={"filterbar-toggle-default"}
                                    onClick={props.onToggleDefault}>
                                    <i className={(props.activePreset.isDefault ? "far fa-star" : "fas fa-star") + " me-1"} />
                                    {translate(props.activePreset.isDefault ? "clear_default" : "set_as_default")}
                                </button>
                                <button
                                    type={"button"}
                                    className={"btn btn-link btn-sm p-0 text-danger"}
                                    data-testid={"filterbar-delete-preset"}
                                    onClick={props.onDeletePreset}>
                                    <i className={"fas fa-trash-alt me-1"} />
                                    {translate("delete_preset")}
                                </button>
                            </div>
                        ) : null}
                        {!props.activePreset ? (
                            <div className={"FilterBar-preset-actions d-flex justify-content-center mt-2"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-link btn-sm p-0"}
                                    data-testid={"filterbar-all-default"}
                                    disabled={props.isAllDefault}
                                    onClick={props.onSetAllAsDefault}>
                                    <i className={(props.isAllDefault ? "fas fa-check" : "fas fa-star") + " me-1"} />
                                    {translate(props.isAllDefault ? "is_default_view" : "set_as_default")}
                                </button>
                            </div>
                        ) : null}
                        {props.onFeedback ? (
                            <div className={"text-center mt-2"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-link btn-sm p-0 FilterBar-feedback-link"}
                                    onClick={props.onFeedback}>
                                    <i className={"fas fa-comment-alt me-1"} />
                                    {translate("send_a_suggestion")}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </OwSidebar>
        </div>,
        document.body
    );
};
